import React, { useEffect, useRef, useState } from 'react';
import { RotateCw, Power } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import useModalEscape from '../../hooks/useModalEscape';
import useModalTransition from '../../hooks/useModalTransition';
import { API_BASE_URL } from '../../config';
import { notifyError, notifySuccess } from '../../notify';
import './SA_ControlCenter.css';
import RenderEnvironment from './RenderEnvironment';
import { readControlCenterResponse } from './controlCenterApi';

export default function SA_ControlCenter() {
  const { getIdToken } = useAuth();
  const [services, setServices] = useState(null);
  const [target, setTarget] = useState('mobile');
  const [confirmTarget, setConfirmTarget] = useState('mobile');
  const [confirmAction, setConfirmAction] = useState('restart');
  const [loading, setLoading] = useState(true);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [statusRefresh, setStatusRefresh] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const shouldRenderConfirmation = useModalTransition(confirming);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [now, setNow] = useState(Date.now());
  const inFlight = useRef(false);
  const cancelRef = useRef(null);
  const restartRef = useRef(null);
  const suspendRef = useRef(null);
  const mainRef = useRef(null);
  const cooldown = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));
  const targetLabel = confirmTarget === 'both' ? 'Both Backends' : confirmTarget === 'admin' ? 'Admin Dashboard Backend' : 'Mobile App Backend';
  const configured = services?.[target]?.configured;
  const suspended = services?.[target]?.state === 'suspended';
  const runningOnRender = services?.[target]?.state === 'on';
  const bothConfigured = services?.mobile?.configured && services?.admin?.configured;
  const bothReady = bothConfigured && services?.mobile?.state !== 'suspended' && services?.admin?.state !== 'suspended';

  useEffect(() => {
    const timer = setInterval(() => { if (!document.hidden) setStatusRefresh(value => value + 1); }, 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let active = true;
    async function load() {
      setCheckingStatus(true);
      try {
        const token = await getIdToken();
        const response = await fetch(`${API_BASE_URL}/api/control-center/services`, {
          headers: { Authorization: `Bearer ${token}` }, signal: AbortSignal.timeout(15000),
        });
        const data = await readControlCenterResponse(response);
        if (!response.ok || !data.ok) throw new Error(data.error || 'Unable to load service configuration.');
        if (active) setServices(data.services);
      } catch (error) {
        if (active) notifyError(error.message || 'Unable to load backend services.');
      } finally { if (active) { setLoading(false); setCheckingStatus(false); } }
    }
    load();
    return () => { active = false; };
  }, [getIdToken, statusRefresh]);

  useEffect(() => {
    if (!cooldownUntil) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [cooldownUntil]);

  useEffect(() => {
    if (confirming && shouldRenderConfirmation) cancelRef.current?.focus();
    if (!confirming && shouldRenderConfirmation) {
      const trigger = confirmAction === 'suspend' ? suspendRef.current : restartRef.current;
      (trigger?.disabled ? mainRef.current : trigger)?.focus();
    }
  }, [confirming, shouldRenderConfirmation, confirmAction]);

  const closeConfirmation = () => {
    if (inFlight.current) return;
    setConfirming(false);
    (confirmAction === 'suspend' ? suspendRef.current : restartRef.current)?.focus();
  };
  useModalEscape(closeConfirmation, confirming);

  async function restart() {
    if (inFlight.current || !confirming) return;
    inFlight.current = true;
    setBusy(true);
    setNow(Date.now());
    setCooldownUntil(Date.now() + 120000);
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/control-center/restart`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ target: confirmTarget, confirmation: `restart-${confirmTarget}` }),
        signal: AbortSignal.timeout(60000),
      });
      const data = await readControlCenterResponse(response);
      if (response.ok && data.ok) {
        notifySuccess(confirmTarget === 'both'
          ? 'Restart requested for both backends.'
          : `Restart requested for the ${confirmTarget === 'mobile' ? 'mobile app' : 'admin dashboard'} backend.`);
      } else if (Array.isArray(data.results) && data.results.length > 0) {
        data.results.forEach(result => {
          const label = result.target === 'mobile' ? 'Mobile App' : 'Admin Dashboard';
          if (result.status === 'accepted') {
            notifySuccess(`${label}: restart requested.`);
          } else {
            notifyError(`${label}: ${result.message || 'Restart could not be confirmed. Check Render before retrying.'}`);
          }
        });
      } else {
        notifyError(data.error || data.message || 'Restart could not be confirmed. Check Render before retrying.');
      }
    } catch (error) {
      notifyError(error.name === 'TimeoutError' || error.name === 'TypeError'
        ? 'Connection interrupted. Restart status is unconfirmed. Check Render before retrying.'
        : error.message || 'Unable to request a restart.');
    } finally {
      inFlight.current = false;
      setBusy(false);
      setConfirming(false);
      setStatusRefresh(value => value + 1);
    }
  }

  async function turnOn() {
    if (resuming || busy || !suspended) return;
    setResuming(true);
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/control-center/resume`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ target }), signal: AbortSignal.timeout(30000),
      });
      const data = await readControlCenterResponse(response);
      if (!response.ok || !data.ok) throw new Error(data.error || 'Render could not turn on this backend.');
      notifySuccess(`${target === 'mobile' ? 'Mobile App' : 'Admin Dashboard'}: turn-on requested. Check Render for readiness.`);
      setStatusRefresh(value => value + 1);
    } catch (error) {
      notifyError(error.message || 'Unable to turn on the backend. Check Render.');
    } finally { setResuming(false); }
  }

  async function turnOff() {
    if (inFlight.current || !confirming || confirmAction !== 'suspend') return;
    inFlight.current = true;
    setBusy(true);
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/control-center/suspend`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ target: confirmTarget, confirmation: `suspend-${confirmTarget}` }),
        signal: AbortSignal.timeout(30000),
      });
      const data = await readControlCenterResponse(response);
      if (!response.ok || !data.ok) throw new Error(data.error || 'Render did not confirm the turn-off request.');
      notifySuccess(`${confirmTarget === 'mobile' ? 'Mobile App' : 'Admin Dashboard'}: turn-off requested.`);
    } catch (error) {
      notifyError(error.name === 'TimeoutError' || error.name === 'TypeError'
        ? 'Connection interrupted. Turn-off status is unconfirmed. Check Render before retrying.'
        : error.message || 'Turn-off status is unconfirmed. Check Render.');
    } finally {
      inFlight.current = false;
      setBusy(false);
      setConfirming(false);
      setStatusRefresh(value => value + 1);
    }
  }

  function askRestart(service) {
    setConfirmTarget(service);
    setConfirmAction('restart');
    setConfirming(true);
  }

  function askTurnOff(service) {
    setConfirmTarget(service);
    setConfirmAction('suspend');
    setConfirming(true);
  }

  return (
    <div className="cc-operations-panel">
      <div className="cc-embedded-main" ref={mainRef} tabIndex={-1}>
        <section className="cc-card cc-backend-services" aria-label="Backend services">
          <div className="cc-service-heading"><div><h2>Backend services</h2><p>Choose a service to manage.</p></div><button type="button" className="cc-status-refresh" onClick={() => setStatusRefresh(value => value + 1)} disabled={checkingStatus}><RotateCw size={14} aria-hidden="true" />Refresh status</button></div>
          <div className="cc-service-picker" aria-label="Backend service">
            {['mobile', 'admin'].map(service => {
              const state = services?.[service]?.state;
              return <button key={service} type="button" className={`cc-service-choice ${target === service ? 'cc-service-choice-active' : ''}`}
                aria-pressed={target === service} disabled={busy || resuming || confirming} onClick={() => setTarget(service)}>
                <span className="cc-service-name">{service === 'mobile' ? 'Mobile App Backend' : 'Admin Dashboard Backend'}</span>
                {(loading || state === 'on' || state === 'suspended') && <span className={`cc-state cc-state-${state || 'unknown'}`}>
                  <span className="cc-status-dot" />{loading ? 'Checking…' : state === 'on' ? 'On' : 'Off · Suspended'}
                </span>}
              </button>;
            })}
          </div>
          <div className="cc-service-actions">
            <div className="cc-action-copy"><strong>{target === 'mobile' ? 'Mobile App Backend' : 'Admin Dashboard Backend'}</strong><span>{suspended ? 'This service is suspended. Turn it on to resume it.' : configured ? 'Restart this service if it is unresponsive.' : 'Add its Render configuration to enable controls.'}</span></div>
            <div className="cc-action-buttons">
              {suspended && <button className="cc-button cc-turn-on" disabled={resuming || busy} onClick={turnOn}><Power size={16} aria-hidden="true" />{resuming ? 'Turning on…' : 'Turn on service'}</button>}
              {runningOnRender && <button ref={suspendRef} className="cc-button cc-turn-off" disabled={loading || busy || resuming} onClick={() => askTurnOff(target)}><Power size={16} aria-hidden="true" />Turn off service</button>}
              <button ref={restartRef} className="cc-button cc-primary" disabled={loading || !configured || busy || resuming || suspended || cooldown > 0} onClick={() => askRestart(target)}>
                <RotateCw size={16} aria-hidden="true" />{busy ? 'Requesting…' : cooldown ? `Retry in ${cooldown}s` : 'Restart service'}
              </button>
              <button className="cc-button cc-restart-both" disabled={loading || !bothReady || busy || resuming || cooldown > 0}
                title={!bothConfigured ? 'Both services must be configured in Render.' : !bothReady ? 'Turn on the suspended service first.' : ''}
                onClick={() => askRestart('both')}>Restart both</button>
            </div>
          </div>
        </section>
        <section className="cc-card cc-environment-card" aria-label="Backend environment variables"><RenderEnvironment target={target} configured={configured} /></section>
      </div>
      {shouldRenderConfirmation && <div className={`cc-overlay modal-transition-${confirming ? 'enter' : 'exit'}`} aria-hidden={!confirming} onClick={closeConfirmation}>
        <div className="cc-dialog" role="dialog" aria-modal="true" aria-busy={busy} aria-labelledby="cc-confirm-title" aria-describedby="cc-confirm-description" onClick={e => e.stopPropagation()}
          onKeyDown={e => {
            if (!confirming) return;
            if (e.key === 'Tab') {
              const buttons = [...e.currentTarget.querySelectorAll('button:not(:disabled)')];
              const first = buttons[0]; const last = buttons[buttons.length - 1];
              if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
              else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
              else if (!buttons.length) e.preventDefault();
            }
          }}>
          <div className="cc-dialog-header">
            <h2 id="cc-confirm-title">{confirmAction === 'suspend' ? `Turn off ${targetLabel}?` : `Restart ${targetLabel}?`}</h2>
            <p id="cc-confirm-description">{confirmAction === 'suspend'
              ? confirmTarget === 'admin'
                ? 'Turning off the admin backend will disconnect this dashboard. You must turn it back on from the Render dashboard.'
                : 'The mobile app backend will stop accepting requests until you turn it back on.'
              : <>Are you sure you want to restart <strong>{confirmTarget === 'both' ? 'both backends' : confirmTarget === 'admin' ? 'the admin dashboard backend' : 'the mobile app backend'}</strong>? Active requests may be interrupted.{confirmTarget !== 'mobile' && ' The admin dashboard may temporarily disconnect.'}</>}</p>
          </div>
          <div className="cc-dialog-actions"><button className="cc-button" ref={cancelRef} disabled={busy || !confirming} onClick={closeConfirmation}>Cancel</button>
            <button className={`cc-button ${confirmAction === 'suspend' ? 'cc-danger' : 'cc-primary'}`} disabled={busy || !confirming} onClick={confirmAction === 'suspend' ? turnOff : restart}>{busy ? 'Requesting…' : confirmAction === 'suspend' ? 'Turn off service' : 'Confirm restart'}</button></div>
        </div>
      </div>}
    </div>
  );
}
